import { useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTasks } from '@/hooks'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { useStreamChat } from '@/hooks/useStreamChat'
import { daysUntil } from '@/lib/utils'
import type { ProactiveSuggestion } from '@/components/AgentActivity/ProactiveCard'

const DISMISS_STORAGE_KEY = 'copilot-dismissed-suggestions'
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000 // 24 hours

function getDismissed(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_STORAGE_KEY) || '{}')
  } catch {
    return {}
  }
}

function isDismissed(id: string): boolean {
  const dismissed = getDismissed()
  const ts = dismissed[id]
  if (!ts) return false
  return Date.now() - ts < DISMISS_DURATION_MS
}

export function useProactiveCards(): {
  suggestions: ProactiveSuggestion[]
  dismiss: (id: string) => void
} {
  const { data: taskData } = useTasks()
  const { user } = useAuthStore()
  const { setChatOpen } = useUiStore()
  const { send } = useStreamChat()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState<string[]>([])
  const isSupervisor = user?.role === 'supervisor'

  const dismiss = useCallback((id: string) => {
    const stored = getDismissed()
    stored[id] = Date.now()
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(stored))
    setDismissed((prev) => [...prev, id])
  }, [])

  const suggestions = useMemo(() => {
    if (!taskData?.items) return []
    const items: ProactiveSuggestion[] = []
    const tasks = taskData.items

    // 1. Overdue tasks with no recent activity
    const overdueTasks = tasks.filter(
      (t) => t.due_date && daysUntil(t.due_date) < 0 && t.status !== 'completed'
    )
    if (overdueTasks.length > 0) {
      const task = overdueTasks[0]!
      const id = `overdue-${task.id}`
      if (!isDismissed(id) && !dismissed.includes(id)) {
        items.push({
          id,
          type: 'overdue',
          title: `"${task.title}" is overdue`,
          description: `Due ${Math.abs(daysUntil(task.due_date!))} days ago. Consider following up or updating the deadline.`,
          actionLabel: 'View Task',
          onAction: () => navigate('/tasks'),
        })
      }
    }

    // 2. Unassigned tasks approaching deadline (supervisor-only — members don't assign tasks)
    if (isSupervisor) {
      const unassignedUrgent = tasks.filter(
        (t) =>
          !t.assignee_id &&
          t.due_date &&
          daysUntil(t.due_date) >= 0 &&
          daysUntil(t.due_date) <= 3 &&
          t.status !== 'completed'
      )
      if (unassignedUrgent.length > 0) {
        const task = unassignedUrgent[0]!
        const id = `unassigned-${task.id}`
        if (!isDismissed(id) && !dismissed.includes(id)) {
          items.push({
            id,
            type: 'unassigned',
            title: `"${task.title}" needs an assignee`,
            description: `Due in ${daysUntil(task.due_date!)} day(s) but no one is assigned yet.`,
            actionLabel: 'Ask Copilot',
            onAction: () => {
              setChatOpen(true)
              send(`Who should I assign "${task.title}" to?`)
            },
          })
        }
      }
    }

    // 3. Workload imbalance (supervisor-only — team rebalancing is a management concern)
    if (isSupervisor) {
      const assigneeCounts: Record<string, number> = {}
      for (const t of tasks.filter((t) => t.assignee_name && t.status !== 'completed')) {
        assigneeCounts[t.assignee_name!] = (assigneeCounts[t.assignee_name!] || 0) + 1
      }
      const names = Object.keys(assigneeCounts)
      if (names.length >= 2) {
        const sorted = names.sort((a, b) => (assigneeCounts[b] ?? 0) - (assigneeCounts[a] ?? 0))
        const maxName = sorted[0]!
        const minName = sorted[sorted.length - 1]!
        const diff = (assigneeCounts[maxName] ?? 0) - (assigneeCounts[minName] ?? 0)
        if (diff >= 3) {
          const id = `workload-${maxName}-${minName}`
          if (!isDismissed(id) && !dismissed.includes(id)) {
            items.push({
              id,
              type: 'workload',
              title: 'Workload imbalance detected',
              description: `${maxName} has ${assigneeCounts[maxName] ?? 0} tasks, ${minName} has ${assigneeCounts[minName] ?? 0}. Consider rebalancing.`,
              actionLabel: 'Check Workload',
              onAction: () => {
                setChatOpen(true)
                send("Who's overloaded right now?")
              },
            })
          }
        }
      }
    }

    // Max 3 cards, priority sorted: overdue > unassigned > workload
    return items.slice(0, 3)
  }, [taskData, dismissed, isSupervisor, navigate, setChatOpen, send])

  return { suggestions, dismiss }
}
